import { getSleeperPlayerMap } from './sleeperKtcLeague.js';

export const DRAFT_POSITIONS = ['ALL', 'QB', 'RB', 'WR', 'TE', 'K', 'DST'];

const POSITION_ORDER = { QB: 0, RB: 1, WR: 2, TE: 3, K: 4, PK: 4, DST: 5, DEF: 5 };

/** Typical redraft roster targets by mid/late draft (skill positions). */
const REDRAFT_TARGETS = {
  sf: { QB: 2, RB: 5, WR: 6, TE: 2, K: 1, DST: 1 },
  oneqb: { QB: 1, RB: 5, WR: 6, TE: 2, K: 1, DST: 1 },
};

export const filterBoard = (board, { draftedIds, position, search }) => {
  const drafted = draftedIds instanceof Set ? draftedIds : new Set(draftedIds);
  const query = (search || '').trim().toLowerCase();

  return board.filter((row) => {
    if (drafted.has(row.id)) return false;
    if (position && position !== 'ALL' && row.position !== position) return false;
    if (query && !row.name.toLowerCase().includes(query)) return false;
    return true;
  });
};

/**
 * How many top-N overall assets remain at each skill position.
 */
export const computePositionScarcity = (board, draftedIds, topN = 48) => {
  const drafted = draftedIds instanceof Set ? draftedIds : new Set(draftedIds);
  const top = board.slice(0, topN);
  const positions = ['QB', 'RB', 'WR', 'TE'];

  return positions.map((pos) => {
    const inTop = top.filter((r) => r.position === pos);
    const remaining = inTop.filter((r) => !drafted.has(r.id)).length;
    const total = inTop.length;
    const ratio = total ? remaining / total : 1;
    let level = 'comfortable';
    if (ratio <= 0.35) level = 'critical';
    else if (ratio <= 0.55) level = 'thin';
    return { position: pos, remaining, total, level };
  });
};

/**
 * Compare roster counts to typical redraft targets and flag needs.
 */
export const computeRosterNeeds = (counts, format = 'oneqb') => {
  const targets = REDRAFT_TARGETS[format] || REDRAFT_TARGETS.oneqb;
  const positions = ['QB', 'RB', 'WR', 'TE', 'K', 'DST'];

  return positions.map((pos) => {
    const have = counts[pos] || 0;
    const target = targets[pos];
    const deficit = Math.max(0, target - have);
    let priority = 'balanced';
    if (deficit >= 3) priority = 'high';
    else if (deficit >= 1) priority = 'medium';
    else if (have >= target + 2) priority = 'surplus';
    return { position: pos, have, target, deficit, priority };
  });
};

const normalizeSleeperPosition = (pos) => {
  if (pos === 'PK') return 'K';
  if (pos === 'DEF') return 'DST';
  return pos;
};

const countRosterPositions = (playerIds, sleeperPlayers) => {
  const counts = { QB: 0, RB: 0, WR: 0, TE: 0, K: 0, DST: 0 };
  for (const pid of playerIds) {
    const p = sleeperPlayers[pid];
    const pos = normalizeSleeperPosition(p?.position || p?.fantasy_positions?.[0]);
    if (pos && counts[pos] != null) counts[pos] += 1;
  }
  return counts;
};

/**
 * Load a Sleeper roster and return position counts for need analysis.
 */
export const loadSleeperRosterCounts = async (leagueId, rosterId) => {
  const lid = leagueId?.trim();
  if (!lid || rosterId == null) {
    throw new Error('Enter a Sleeper league ID and pick your team.');
  }

  const [rostersRes, sleeperPlayers] = await Promise.all([
    fetch(`https://api.sleeper.app/v1/league/${lid}/rosters`),
    getSleeperPlayerMap(),
  ]);

  if (!rostersRes.ok) {
    throw new Error('Could not load this Sleeper league.');
  }

  const rosters = await rostersRes.json();
  const roster = rosters.find((r) => String(r.roster_id) === String(rosterId));
  if (!roster) throw new Error('Team not found in this league.');

  const ids = new Set();
  ['players', 'starters', 'reserve', 'taxi'].forEach((key) => {
    (roster[key] || []).forEach((id) => {
      if (id && id !== '0') ids.add(String(id));
    });
  });

  return countRosterPositions(ids, sleeperPlayers);
};

/**
 * League settings we need to shape the board: roster slots, size, scoring.
 */
export const fetchLeagueMeta = async (leagueId) => {
  const lid = leagueId?.trim();
  if (!lid) throw new Error('Enter your Sleeper league ID.');

  const res = await fetch(`https://api.sleeper.app/v1/league/${lid}`);
  if (!res.ok) throw new Error('Could not load this league. Double-check the league ID.');

  const league = await res.json();
  if (!league?.league_id) throw new Error('Could not load this league. Double-check the league ID.');

  const ppr = Number(league?.scoring_settings?.rec ?? 0);
  return {
    leagueId: league.league_id,
    ppr,
    scoringSettings: league?.scoring_settings || {},
    name: league.name || 'Sleeper league',
    season: league.season || '',
    status: league.status || '',
    totalRosters: Number(league.total_rosters) || 0,
    rosterPositions: league.roster_positions || [],
    scoringLabel: ppr >= 1 ? 'PPR' : ppr > 0 ? `${ppr} PPR` : 'Standard',
  };
};

/** Resolve a Sleeper username (or user ID) to a user ID. */
export const fetchSleeperUserId = async (usernameOrId) => {
  const handle = usernameOrId?.trim();
  if (!handle) return '';
  if (/^\d{6,}$/.test(handle)) return handle;

  const res = await fetch(`https://api.sleeper.app/v1/user/${encodeURIComponent(handle)}`);
  if (!res.ok) return '';
  const user = await res.json();
  return user?.user_id ? String(user.user_id) : '';
};

/**
 * Players already on a Sleeper roster (keepers, dynasty holds), mapped onto
 * board rows so they count toward lineup needs before the draft starts.
 */
export const loadRosterBoardRows = async (leagueId, rosterId, board) => {
  const lid = leagueId?.trim();
  if (!lid || rosterId == null) return [];

  const [rostersRes, sleeperPlayers] = await Promise.all([
    fetch(`https://api.sleeper.app/v1/league/${lid}/rosters`),
    getSleeperPlayerMap(),
  ]);
  if (!rostersRes.ok) return [];

  const rosters = await rostersRes.json();
  const roster = (rosters || []).find((r) => String(r.roster_id) === String(rosterId));
  if (!roster) return [];

  const ids = new Set();
  ['players', 'starters', 'reserve', 'taxi'].forEach((key) => {
    (roster[key] || []).forEach((id) => {
      if (id && id !== '0') ids.add(String(id));
    });
  });

  const byName = new Map(board.map((row) => [normalizeMatchKey(row.name), row]));
  const rows = [];
  for (const pid of ids) {
    const p = sleeperPlayers[pid];
    const name = p?.full_name || [p?.first_name, p?.last_name].filter(Boolean).join(' ');
    const row = byName.get(normalizeMatchKey(name));
    if (row) rows.push(row);
  }
  return rows;
};

const normalizeMatchKey = (name) =>
  (name || '')
    .toLowerCase()
    .replace(/\./g, '')
    .replace(/\b(jr|sr|iii|ii|iv)\b/g, '')
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

export const fetchSleeperTeams = async (leagueId) => {
  const lid = leagueId?.trim();
  if (!lid) throw new Error('Enter your Sleeper league ID.');

  const [usersRes, rostersRes] = await Promise.all([
    fetch(`https://api.sleeper.app/v1/league/${lid}/users`),
    fetch(`https://api.sleeper.app/v1/league/${lid}/rosters`),
  ]);

  if (!usersRes.ok || !rostersRes.ok) {
    throw new Error('Could not load this league. Double-check the league ID.');
  }

  const users = await usersRes.json();
  const rosters = await rostersRes.json();
  if (!Array.isArray(rosters) || !rosters.length) {
    throw new Error('This league has no rosters yet.');
  }

  const userById = new Map((users || []).map((u) => [u.user_id, u]));
  const list = rosters.map((r) => {
    const owner = r?.owner_id ? userById.get(r.owner_id) : null;
    const teamName =
      owner?.metadata?.team_name ||
      owner?.display_name ||
      owner?.username ||
      `Roster ${r.roster_id}`;
    return {
      rosterId: r.roster_id,
      teamName,
      ownerId: r?.owner_id || null,
      username: owner?.username || '',
      displayName: owner?.display_name || '',
    };
  });

  list.sort((a, b) => Number(a.rosterId) - Number(b.rosterId));
  return list;
};

/**
 * Next tier cliff: how many picks until a meaningful value drop.
 *
 * The gap is measured in points over replacement, so it reads as "waiting this
 * long costs you eleven points" rather than in some vendor's index units.
 */
export const nextTierCliff = (available) => {
  if (available.length < 2) return null;
  const first = available[0];
  const nextTierIdx = available.findIndex((r) => r.tier > first.tier);
  if (nextTierIdx <= 0) return null;
  const next = available[nextTierIdx];
  const gap =
    first.vor != null && next?.vor != null
      ? Math.round((first.vor - next.vor) * 10) / 10
      : null;
  return {
    player: first.name,
    picksUntilCliff: nextTierIdx,
    nextTierFirst: next?.name,
    valueGap: gap,
  };
};

/**
 * Sortable board columns.
 *
 * The board answers one question per column and stops. It used to carry our
 * projection, its value over replacement, the cost of waiting a turn, the
 * model's edge, ESPN's rank and the gap to it — six numbers derived from the
 * same two, four of which nobody looked at while a clock was running.
 *
 * What is left is the consensus, the market, the distance between them, and
 * the three facts that explain the distance: how old he is, what he did last
 * year, and whether his offence scores. The model has not gone anywhere — it
 * still prices the auction column and drives the recommendations — it just
 * stopped taking up six columns to say so.
 *
 * `get` pulls the comparable value; null means "no value at this column" and
 * always sorts last regardless of direction, so a kicker with no ADP never
 * crowds out the players who have one.
 *
 * `dir` is the direction a first click should apply — ranks read best-first
 * ascending (RB1 before RB2), dollars best-first descending.
 */
export const BOARD_COLUMNS = {
  rank: { label: '#', get: (r) => r.boardRank, dir: 'asc', numeric: true },
  name: { label: 'Player', get: (r) => r.name?.toLowerCase() ?? null, dir: 'asc' },
  team: { label: 'Team', get: (r) => r.team?.toLowerCase() ?? null, dir: 'asc' },
  ecr: { label: 'ECR', get: (r) => r.ecrRank ?? null, dir: 'asc', numeric: true },
  // Groups by position, then orders within it, so one click gives you the
  // whole positional board in consensus order.
  posRank: {
    label: 'Pos',
    get: (r) =>
      r.ecrPosRank == null
        ? null
        : `${String(POSITION_ORDER[r.position] ?? 9)}|${String(r.ecrPosRank).padStart(4, '0')}`,
    dir: 'asc',
  },
  adp: { label: 'ADP', get: (r) => r.adp ?? null, dir: 'asc', numeric: true },
  // First click sorts the fallers to the top — the players the room lets
  // slide, which is the side of this column you can act on.
  adpGap: { label: 'vs ADP', get: (r) => r.adpGap ?? null, dir: 'desc', numeric: true },
  age: { label: 'Age', get: (r) => r.age ?? null, dir: 'asc', numeric: true },
  offense: { label: 'Off', get: (r) => r.teamOffenseRank ?? null, dir: 'asc', numeric: true },
  // Position first again: "who finished RB1 last year" is the question, and
  // an unqualified 1 next to a receiver's 1 is not an answer.
  lastYear: {
    label: 'LY',
    get: (r) =>
      r.lastPosRank == null
        ? null
        : `${String(POSITION_ORDER[r.position] ?? 9)}|${String(r.lastPosRank).padStart(4, '0')}`,
    dir: 'asc',
  },
  auction: { label: '$', get: (r) => r.auction ?? null, dir: 'desc', numeric: true },
};

const compareValues = (a, b) => {
  if (typeof a === 'string' || typeof b === 'string') {
    return String(a).localeCompare(String(b));
  }
  return a - b;
};

/**
 * Sort rows for display only — the board's ranking order is a separate concern
 * and still drives survival odds and recommendations.
 */
export const sortBoardRows = (rows, sortKey, sortDir = 'asc') => {
  const column = BOARD_COLUMNS[sortKey];
  if (!column) return rows;

  const factor = sortDir === 'desc' ? -1 : 1;

  return [...rows].sort((rowA, rowB) => {
    const a = column.get(rowA);
    const b = column.get(rowB);

    // Missing values sink to the bottom either way round.
    if (a == null && b == null) return (rowA.boardRank ?? 0) - (rowB.boardRank ?? 0);
    if (a == null) return 1;
    if (b == null) return -1;

    const diff = compareValues(a, b);
    if (diff !== 0) return diff * factor;
    // Ties fall back to board rank so the order stays stable and meaningful.
    return (rowA.boardRank ?? 0) - (rowB.boardRank ?? 0);
  });
};

/** Next sort state when a header is clicked: first click uses the column's natural direction. */
export const nextSortState = (column, current) => {
  const def = BOARD_COLUMNS[column];
  if (!def) return current;
  if (current.key !== column) return { key: column, dir: def.dir };
  if (current.dir === def.dir) return { key: column, dir: def.dir === 'asc' ? 'desc' : 'asc' };
  // Third click clears back to the board's own ranking.
  return { key: null, dir: 'asc' };
};
